import type { Goal, HealthArea, Routine } from "instantdb/types";

// Goals
import { NUTRITION_GOALS } from "@/seed/goals/LibraryNutritionGoals";
import { ENERGY_GOALS } from "@/seed/goals/LibraryEnergyGoals";
import { FITNESS_GOALS } from "@/seed/goals/LibraryFitnessGoals";
import { HEART_GOALS } from "@/seed/goals/LibraryHeartGoals";
import { HYDRATION_GOALS } from "@/seed/goals/LibraryHydrationGoals";
import { MENTAL_GOALS } from "@/seed/goals/LibraryMentalGoals";
import { MOOD_GOALS } from "@/seed/goals/LibraryMoodGoals";
import { MOVEMENT_GOALS } from "@/seed/goals/LibraryMovementGoals";
import { SLEEP_GOALS } from "@/seed/goals/LibrarySleepGoals";
import { STRESS_GOALS } from "@/seed/goals/LibraryStressGoals";
import { WEIGHT_GOALS } from "@/seed/goals/LibraryWeightGoals";

// Routines
import { SLEEP_ROUTINES } from "@/seed/routines/LibrarySleepRoutines";
import { NUTRITION_ROUTINES } from "@/seed/routines/LibraryDietRoutines";
import { FITNESS_ROUTINES } from "@/seed/routines/LibraryFitnessRoutines";
import { MOOD_ROUTINES } from "@/seed/routines/LibraryMoodRoutines";
import { STRESS_ROUTINES } from "@/seed/routines/LibraryStressRoutines";
import { HEART_ROUTINES } from "@/seed/routines/LibraryHeartRoutines";
import { ENERGY_ROUTINES } from "@/seed/routines/LibraryEnergyRoutines";
import { HYDRATION_ROUTINES } from "@/seed/routines/LibraryHydrationRoutines";
import { WEIGHT_ROUTINES } from "@/seed/routines/LibraryWeightRoutines";
import { MENTAL_ROUTINES } from "@/seed/routines/LibraryMentalRoutines";
import { MOVEMENT_ROUTINES } from "@/seed/routines/LibraryMovementRoutines";

export const HEALTH_AREA = [
  "nutrition",
  "energy",
  "fitness",
  "heart",
  "hydration",
  "mental",
  "mood",
  "movement",
  "sleep",
  "stress",
  "weight",
] as const;

export const HEALTH_AREA_MAPPING: Record<
  (typeof HEALTH_AREA)[number],
  Partial<HealthArea> & {
    goals: Partial<Goal>[];
    routines: Partial<Routine>[];
  }
> = {
  nutrition: {
    slug: "nutrition",
    title: "health_areas.nutrition.title",
    description: "health_areas.nutrition.description",
    symbol: "🍎",
    color: "bg-[#9B72CF]/15",
    goals: NUTRITION_GOALS,
    routines: NUTRITION_ROUTINES,
  },
  energy: {
    slug: "energy",
    title: "health_areas.energy.title",
    description: "health_areas.energy.description",
    symbol: "⚡",
    color: "bg-[#FFD700]/15",
    goals: ENERGY_GOALS,
    routines: ENERGY_ROUTINES,
  },
  fitness: {
    slug: "fitness",
    title: "health_areas.fitness.title",
    description: "health_areas.fitness.description",
    symbol: "💪",
    color: "bg-[#45B7D1]/15",
    goals: FITNESS_GOALS,
    routines: FITNESS_ROUTINES,
  },
  heart: {
    slug: "heart",
    title: "health_areas.heart.title",
    description: "health_areas.heart.description",
    symbol: "❤️",
    color: "bg-[#FF6B6B]/15",
    goals: HEART_GOALS,
    routines: HEART_ROUTINES,
  },
  hydration: {
    slug: "hydration",
    title: "health_areas.hydration.title",
    description: "health_areas.hydration.description",
    symbol: "💧",
    color: "bg-[#50C878]/15",
    goals: HYDRATION_GOALS,
    routines: HYDRATION_ROUTINES,
  },
  mental: {
    slug: "mental",
    title: "health_areas.mental.title",
    description: "health_areas.mental.description",
    symbol: "🧠",
    color: "bg-[#9B72CF]/15",
    goals: MENTAL_GOALS,
    routines: MENTAL_ROUTINES,
  },
  mood: {
    slug: "mood",
    title: "health_areas.mood.title",
    description: "health_areas.mood.description",
    symbol: "😊",
    color: "bg-[#96CEB4]/15",
    goals: MOOD_GOALS,
    routines: MOOD_ROUTINES,
  },
  movement: {
    slug: "movement",
    title: "health_areas.movement.title",
    description: "health_areas.movement.description",
    symbol: "🏃",
    color: "bg-[#96CEB4]/15",
    goals: MOVEMENT_GOALS,
    routines: MOVEMENT_ROUTINES,
  },
  sleep: {
    slug: "sleep",
    title: "health_areas.sleep.title",
    description: "health_areas.sleep.description",
    symbol: "💤",
    color: "bg-[#FF6B6B]/15",
    goals: SLEEP_GOALS,
    routines: SLEEP_ROUTINES,
  },
  stress: {
    slug: "stress",
    title: "health_areas.stress.title",
    description: "health_areas.stress.description",
    symbol: "😌",
    color: "bg-[#FF8FB1]/15",
    goals: STRESS_GOALS,
    routines: STRESS_ROUTINES,
  },
  weight: {
    slug: "weight",
    title: "health_areas.weight.title",
    description: "health_areas.weight.description",
    symbol: "⚖️",
    color: "bg-[#6C63FF]/15",
    goals: WEIGHT_GOALS,
    routines: WEIGHT_ROUTINES,
  },
} as const;
